// Notification Preferences & Push Opt-In Page
// ─────────────────────────────────────────────────────────────
// Beautiful, modern notification settings page with:
// - Push notification opt-in with animated permission flow
// - Per-channel toggles (push, email, SMS)
// - Per-category granular controls
// - Quiet hours configuration
// - In-app notification history

import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Bell,
  BellRing,
  BellOff,
  Mail,
  MessageSquare,
  Smartphone,
  ShoppingBag,
  Store,
  Tag,
  Shield,
  Clock,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Loader2,
  Sparkles,
  Package,
  Truck,
  AlertCircle,
  CheckCircle2,
  Info,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useNotifications } from '../../hooks/useNotifications';
import { cn } from '../../lib/utils';
import type {
  NotificationPreferences,
  NotificationCategory,
  NotificationChannel,
} from '../../types/notifications';
import { NOTIFICATION_CATEGORIES, DEFAULT_NOTIFICATION_PREFERENCES } from '../../types/notifications';

// ── Toggle Component ────────────────────────────────────────

function Toggle({
  checked,
  onChange,
  disabled,
  size = 'default',
}: {
  checked: boolean;
  onChange: (val: boolean) => void;
  disabled?: boolean;
  size?: 'default' | 'large';
}) {
  const sizeClasses = size === 'large'
    ? 'w-14 h-8'
    : 'w-12 h-7';
  const dotClasses = size === 'large'
    ? 'w-6 h-6'
    : 'w-5 h-5';
  const translateChecked = size === 'large'
    ? 'translate-x-7'
    : 'translate-x-6';

  return (
    <button
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={cn(
        'rounded-full transition-all duration-300 relative',
        sizeClasses,
        checked ? 'bg-primary shadow-lg shadow-primary/25' : 'bg-gray-200',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      <motion.div
        layout
        className={cn(
          'absolute top-1 rounded-full bg-white shadow-sm',
          dotClasses,
          checked ? translateChecked : 'translate-x-1'
        )}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      />
    </button>
  );
}

// ── Category Toggle Row ─────────────────────────────────────

function CategoryToggle({
  label,
  description,
  icon: Icon,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description: string;
  icon: React.ElementType;
  checked: boolean;
  onChange: (val: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className={cn(
      'flex items-center justify-between py-3.5 px-1',
      disabled && 'opacity-50'
    )}>
      <div className="flex items-center space-x-3 flex-1 min-w-0">
        <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
          <Icon className="h-4.5 w-4.5 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="font-medium text-heading text-sm">{label}</p>
          <p className="text-xs text-body mt-0.5 truncate">{description}</p>
        </div>
      </div>
      <Toggle checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  );
}

// ── Expandable Section ──────────────────────────────────────

function ExpandableSection({
  title,
  icon: Icon,
  children,
  defaultOpen = false,
  badge,
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badge?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Card className="overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-heading">{title}</p>
            {badge && (
              <span className="text-xs text-primary font-medium">{badge}</span>
            )}
          </div>
        </div>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="h-5 w-5 text-gray-400" />
        </motion.div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <CardContent className="pt-0 pb-2 px-4 divide-y divide-gray-50">
              {children}
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

// ── Status Banner ───────────────────────────────────────────

function PushStatusBanner({
  status,
  pushEnabled,
  onEnable,
  onDisable,
  loading,
}: {
  status: 'granted' | 'denied' | 'default' | 'unsupported';
  pushEnabled: boolean;
  onEnable: () => void;
  onDisable: () => void;
  loading: boolean;
}) {
  if (status === 'unsupported') {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-amber-900">Push Not Supported</p>
              <p className="text-sm text-amber-700 mt-1">
                Your browser doesn't support push notifications. Try Chrome, Firefox, or Edge for the best experience.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (status === 'denied') {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <BellOff className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-900">Push Notifications Blocked</p>
              <p className="text-sm text-red-700 mt-1">
                You've blocked notifications for this site. To re-enable, click the lock icon in your browser's address bar and allow notifications.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (status === 'granted' && pushEnabled) {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-start space-x-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-green-900">Push Notifications Active</p>
                <p className="text-sm text-green-700 mt-1">
                  You'll receive real-time alerts about your orders.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={onDisable}
              className="text-green-700 border-green-300 hover:bg-green-100 flex-shrink-0"
            >
              Disable
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Default: not yet opted in
  return null;
}

// ═══════════════════════════════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════════════════════════════

export function NotificationPreferencesPage() {
  const navigate = useNavigate();
  const {
    pushPermission,
    vapidConfigured,
    pushEnabled,
    requestingPush,
    preferences,
    preferencesLoading,
    enablePush,
    disablePush,
    updatePreferences,
    unreadCount,
  } = useNotifications();

  // Local copy of preferences for optimistic UI
  const [localPrefs, setLocalPrefs] = useState<NotificationPreferences | null>(null);
  const [saving, setSaving] = useState(false);
  const [showPushOptIn, setShowPushOptIn] = useState(false);
  const [pushOptInStep, setPushOptInStep] = useState<'intro' | 'requesting' | 'success' | 'denied'>('intro');

  // Sync preferences
  useEffect(() => {
    if (preferences) {
      setLocalPrefs(preferences);
    }
  }, [preferences]);

  // Show push opt-in if not enabled and permission is default
  useEffect(() => {
    if (pushPermission === 'default' && !pushEnabled) {
      setShowPushOptIn(true);
    }
  }, [pushPermission, pushEnabled]);

  const prefs = localPrefs || DEFAULT_NOTIFICATION_PREFERENCES;

  // ── Save preference changes ───────────────────────────────

  const handlePrefsChange = async (newPrefs: NotificationPreferences) => {
    setLocalPrefs(newPrefs);

    try {
      setSaving(true);
      await updatePreferences(newPrefs);
    } catch (error) {
      // Revert on error
      setLocalPrefs(preferences);
    } finally {
      setSaving(false);
    }
  };

  const toggleChannel = (channel: NotificationChannel, value: boolean) => {
    const newPrefs = {
      ...prefs,
      channels: { ...prefs.channels, [channel]: value },
    };
    handlePrefsChange(newPrefs);
  };

  const toggleCategory = (category: NotificationCategory, value: boolean) => {
    const newPrefs = {
      ...prefs,
      categories: { ...prefs.categories, [category]: value },
    };
    handlePrefsChange(newPrefs);
  };

  const toggleMaster = (value: boolean) => {
    const newPrefs = { ...prefs, enabled: value };
    handlePrefsChange(newPrefs);
  };

  const toggleQuietHours = (value: boolean) => {
    const newPrefs = {
      ...prefs,
      quietHours: {
        ...prefs.quietHours!,
        enabled: value,
      },
    };
    handlePrefsChange(newPrefs);
  };

  // ── Push opt-in flow ──────────────────────────────────────

  const handleEnablePush = async () => {
    setPushOptInStep('requesting');
    const success = await enablePush();
    setPushOptInStep(success ? 'success' : 'denied');

    if (success) {
      setTimeout(() => setShowPushOptIn(false), 2000);
    }
  };

  // ── Category icon mapper ──────────────────────────────────
  const getCategoryIcon = (key: NotificationCategory): React.ElementType => {
    switch (key) {
      case 'order_confirmation': return ShoppingBag;
      case 'order_approved': return Check;
      case 'driver_assigned': return Truck;
      case 'order_picked_up': return Package;
      case 'out_for_delivery': return Truck;
      case 'order_delivered': return CheckCircle2;
      case 'order_cancelled': return X;
      case 'refund_processed': return AlertCircle;
      case 'new_order': return ShoppingBag;
      case 'order_reminder': return Clock;
      case 'promotions': return Tag;
      case 'new_arrivals': return Sparkles;
      case 'account_activity': return Smartphone;
      case 'security_alerts': return Shield;
      default: return Bell;
    }
  };

  // ── Group categories ──────────────────────────────────────

  const orderCategories = NOTIFICATION_CATEGORIES.filter((c) => c.group === 'orders');
  const businessCategories = NOTIFICATION_CATEGORIES.filter((c) => c.group === 'business');
  const marketingCategories = NOTIFICATION_CATEGORIES.filter((c) => c.group === 'marketing');
  const accountCategories = NOTIFICATION_CATEGORIES.filter((c) => c.group === 'account');

  if (preferencesLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="mt-3 text-body">Loading preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/settings', { replace: true })}
                className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ArrowLeft className="h-6 w-6 text-heading" />
              </button>
              <h1 className="ml-4 text-xl font-serif text-heading">Notifications</h1>
            </div>
            {saving && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center space-x-2 text-primary"
              >
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm font-medium">Saving...</span>
              </motion.div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6 pb-24">
        {/* ── Push Notification Opt-In Hero ─────────────────── */}
        <AnimatePresence>
          {showPushOptIn && pushPermission === 'default' && !pushEnabled && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.98 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="overflow-hidden border-0 shadow-xl">
                {/* Gradient hero */}
                <div className="relative bg-gradient-to-br from-primary via-purple-500 to-purple-700 p-8 text-white">
                  {/* Background decoration */}
                  <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/5 rounded-full" />
                    <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/5 rounded-full" />
                    <div className="absolute top-1/2 right-1/4 w-20 h-20 bg-white/5 rounded-full" />
                  </div>

                  <div className="relative">
                    {pushOptInStep === 'intro' && (
                      <div className="text-center">
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: 'spring', delay: 0.1 }}
                          className="w-20 h-20 rounded-2xl bg-white/15 backdrop-blur-sm mx-auto flex items-center justify-center mb-5"
                        >
                          <BellRing className="h-10 w-10 text-white" />
                        </motion.div>
                        <h2 className="text-2xl font-serif mb-3">Stay in the Loop</h2>
                        <p className="text-white/80 text-sm max-w-sm mx-auto mb-6 leading-relaxed">
                          Get instant updates about your orders — from confirmation to delivery — right on your device.
                        </p>

                        <div className="space-y-3 max-w-xs mx-auto mb-8">
                          {[
                            { icon: ShoppingBag, text: 'Order confirmations & approvals' },
                            { icon: Truck, text: 'Real-time delivery tracking' },
                            { icon: Tag, text: 'Exclusive deals & new arrivals' },
                          ].map((item, i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.2 + i * 0.1 }}
                              className="flex items-center space-x-3 text-left"
                            >
                              <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
                                <item.icon className="h-4 w-4" />
                              </div>
                              <span className="text-sm text-white/90">{item.text}</span>
                            </motion.div>
                          ))}
                        </div>

                        <div className="flex flex-col space-y-3">
                          <Button
                            onClick={handleEnablePush}
                            className="bg-white text-primary hover:bg-white/90 font-semibold py-3 rounded-xl shadow-lg"
                          >
                            <Bell className="h-5 w-5 mr-2" />
                            Enable Push Notifications
                          </Button>
                          <button
                            onClick={() => setShowPushOptIn(false)}
                            className="text-white/60 text-sm hover:text-white/80 transition-colors"
                          >
                            Maybe later
                          </button>
                        </div>
                      </div>
                    )}

                    {pushOptInStep === 'requesting' && (
                      <div className="text-center py-8">
                        <Loader2 className="h-12 w-12 animate-spin text-white mx-auto mb-4" />
                        <p className="text-white/80">
                          Please allow notifications in the browser popup...
                        </p>
                      </div>
                    )}

                    {pushOptInStep === 'success' && (
                      <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="text-center py-8"
                      >
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: 'spring', delay: 0.1 }}
                          className="w-16 h-16 rounded-full bg-green-400/20 mx-auto flex items-center justify-center mb-4"
                        >
                          <Check className="h-8 w-8 text-white" />
                        </motion.div>
                        <h3 className="text-xl font-serif mb-2">You're All Set!</h3>
                        <p className="text-white/80 text-sm">
                          Push notifications are now enabled.
                        </p>
                      </motion.div>
                    )}

                    {pushOptInStep === 'denied' && (
                      <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="text-center py-4"
                      >
                        <BellOff className="h-10 w-10 text-white/60 mx-auto mb-3" />
                        <h3 className="text-lg font-serif mb-2">Notifications Blocked</h3>
                        <p className="text-white/70 text-sm max-w-sm mx-auto mb-4">
                          It looks like notifications were blocked. You can still receive email alerts. To enable push later, update your browser settings.
                        </p>
                        <Button
                          variant="outline"
                          onClick={() => setShowPushOptIn(false)}
                          className="border-white/30 text-white hover:bg-white/10"
                        >
                          Got it
                        </Button>
                      </motion.div>
                    )}
                  </div>
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Push Status Banner ────────────────────────────── */}
        <PushStatusBanner
          status={pushPermission}
          pushEnabled={pushEnabled}
          onEnable={handleEnablePush}
          onDisable={disablePush}
          loading={requestingPush}
        />

        {!vapidConfigured && pushPermission !== 'unsupported' && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-amber-900">Push Setup Incomplete</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Web push is not configured yet. An admin must set the Firebase VAPID key in Admin → API Keys or in the deployment environment.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Master Toggle ────────────────────────────────── */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className={cn(
                  'w-12 h-12 rounded-xl flex items-center justify-center transition-colors duration-300',
                  prefs.enabled ? 'bg-primary/10' : 'bg-gray-100'
                )}>
                  {prefs.enabled ? (
                    <Bell className="h-6 w-6 text-primary" />
                  ) : (
                    <BellOff className="h-6 w-6 text-gray-400" />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-heading text-lg">All Notifications</p>
                  <p className="text-sm text-body">
                    {prefs.enabled ? 'Receiving notifications' : 'All notifications paused'}
                  </p>
                </div>
              </div>
              <Toggle
                checked={prefs.enabled}
                onChange={toggleMaster}
                size="large"
              />
            </div>
          </CardContent>
        </Card>

        {/* ── Channels Section ─────────────────────────────── */}
        <div>
          <h2 className="text-sm font-medium text-body uppercase tracking-wider mb-3 px-1">
            Notification Channels
          </h2>
          <Card>
            <CardContent className="p-4 divide-y divide-gray-50">
              {/* Push */}
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                    <Smartphone className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-heading">Push Notifications</p>
                    <p className="text-xs text-body mt-0.5">
                      {pushPermission === 'granted'
                        ? 'Instant alerts on your device'
                        : pushPermission === 'denied'
                          ? 'Blocked by browser — update in settings'
                          : pushPermission === 'unsupported'
                            ? 'Not supported in this browser'
                            : !vapidConfigured
                              ? 'Setup required before enabling'
                              : 'Not yet enabled'}
                    </p>
                  </div>
                </div>
                {pushPermission === 'granted' ? (
                  <Toggle
                    checked={prefs.channels.push}
                    onChange={(val) => val ? enablePush() : disablePush()}
                    disabled={!prefs.enabled}
                  />
                ) : pushPermission === 'default' ? (
                  <Button
                    size="sm"
                    onClick={handleEnablePush}
                    disabled={requestingPush || !prefs.enabled || !vapidConfigured}
                    className="rounded-lg"
                  >
                    {requestingPush ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Enable'
                    )}
                  </Button>
                ) : pushPermission === 'unsupported' ? (
                  <span className="text-xs text-amber-600 font-medium">Unsupported</span>
                ) : (
                  <span className="text-xs text-red-500 font-medium">Blocked</span>
                )}
              </div>

              {/* Email */}
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-heading">Email</p>
                    <p className="text-xs text-body mt-0.5">Order receipts and updates</p>
                  </div>
                </div>
                <Toggle
                  checked={prefs.channels.email}
                  onChange={(val) => toggleChannel('email', val)}
                  disabled={!prefs.enabled}
                />
              </div>

              {/* SMS */}
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-heading">SMS</p>
                    <p className="text-xs text-body mt-0.5">Text messages for critical alerts</p>
                  </div>
                </div>
                <Toggle
                  checked={prefs.channels.sms}
                  onChange={(val) => toggleChannel('sms', val)}
                  disabled={!prefs.enabled}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── Category Sections ────────────────────────────── */}
        <div>
          <h2 className="text-sm font-medium text-body uppercase tracking-wider mb-3 px-1">
            What You'll Receive
          </h2>

          <div className="space-y-3">
            {/* Order Updates */}
            <ExpandableSection
              title="Order Updates"
              icon={ShoppingBag}
              defaultOpen={true}
              badge={`${orderCategories.filter((c) => prefs.categories[c.key] !== false).length}/${orderCategories.length} enabled`}
            >
              {orderCategories.map((cat) => (
                <CategoryToggle
                  key={cat.key}
                  label={cat.label}
                  description={cat.description}
                  icon={getCategoryIcon(cat.key)}
                  checked={prefs.categories[cat.key] !== false}
                  onChange={(val) => toggleCategory(cat.key, val)}
                  disabled={!prefs.enabled}
                />
              ))}
            </ExpandableSection>

            {/* Business Alerts (show for business users) */}
            <ExpandableSection
              title="Business Alerts"
              icon={Store}
              badge={`${businessCategories.filter((c) => prefs.categories[c.key] !== false).length}/${businessCategories.length} enabled`}
            >
              {businessCategories.map((cat) => (
                <CategoryToggle
                  key={cat.key}
                  label={cat.label}
                  description={cat.description}
                  icon={getCategoryIcon(cat.key)}
                  checked={prefs.categories[cat.key] !== false}
                  onChange={(val) => toggleCategory(cat.key, val)}
                  disabled={!prefs.enabled}
                />
              ))}
            </ExpandableSection>

            {/* Marketing */}
            <ExpandableSection
              title="Promotions & Marketing"
              icon={Tag}
              badge={`${marketingCategories.filter((c) => prefs.categories[c.key] !== false).length}/${marketingCategories.length} enabled`}
            >
              {marketingCategories.map((cat) => (
                <CategoryToggle
                  key={cat.key}
                  label={cat.label}
                  description={cat.description}
                  icon={getCategoryIcon(cat.key)}
                  checked={prefs.categories[cat.key] !== false}
                  onChange={(val) => toggleCategory(cat.key, val)}
                  disabled={!prefs.enabled}
                />
              ))}
              <div className="flex items-start space-x-2 py-3 px-1">
                <Info className="h-4 w-4 text-body mt-0.5 flex-shrink-0" />
                <p className="text-xs text-body">
                  Marketing notifications are opt-in only. We won't send you promotions unless you choose to receive them.
                </p>
              </div>
            </ExpandableSection>

            {/* Account & Security */}
            <ExpandableSection
              title="Account & Security"
              icon={Shield}
              badge={`${accountCategories.filter((c) => prefs.categories[c.key] !== false).length}/${accountCategories.length} enabled`}
            >
              {accountCategories.map((cat) => (
                <CategoryToggle
                  key={cat.key}
                  label={cat.label}
                  description={cat.description}
                  icon={getCategoryIcon(cat.key)}
                  checked={prefs.categories[cat.key] !== false}
                  onChange={(val) => toggleCategory(cat.key, val)}
                  disabled={!prefs.enabled}
                />
              ))}
            </ExpandableSection>
          </div>
        </div>

        {/* ── Quiet Hours ──────────────────────────────────── */}
        <div>
          <h2 className="text-sm font-medium text-body uppercase tracking-wider mb-3 px-1">
            Do Not Disturb
          </h2>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-medium text-heading">Quiet Hours</p>
                    <p className="text-xs text-body mt-0.5">Pause push notifications during set hours</p>
                  </div>
                </div>
                <Toggle
                  checked={prefs.quietHours?.enabled || false}
                  onChange={toggleQuietHours}
                  disabled={!prefs.enabled || !prefs.channels.push}
                />
              </div>

              <AnimatePresence>
                {prefs.quietHours?.enabled && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="bg-indigo-50/50 rounded-xl p-4 flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="text-center">
                          <p className="text-xs text-body uppercase">From</p>
                          <p className="text-lg font-semibold text-heading mt-1">
                            {prefs.quietHours?.start || '22:00'}
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-300" />
                        <div className="text-center">
                          <p className="text-xs text-body uppercase">Until</p>
                          <p className="text-lg font-semibold text-heading mt-1">
                            {prefs.quietHours?.end || '07:00'}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-body">
                        {prefs.quietHours?.timezone?.split('/').pop()?.replace('_', ' ') || 'Local Time'}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>
        </div>

        {/* ── Info Footer ──────────────────────────────────── */}
        <div className="bg-gray-100 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <Info className="h-5 w-5 text-body mt-0.5 flex-shrink-0" />
            <div className="text-sm text-body space-y-1">
              <p>
                <strong className="text-heading">Your privacy matters.</strong> We only send notifications you've opted into. You can change these settings at any time.
              </p>
              <p>
                Critical security alerts (like suspicious login activity) may still be sent even if notifications are paused.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
